import type { Locale, Translator } from './index';

const INTL_LOCALE: Record<Locale, string> = { en: 'en-GB', de: 'de-DE' };

export function intlLocale(locale: Locale): string {
  return INTL_LOCALE[locale];
}

export function formatNumber(value: number, locale: Locale): string {
  return new Intl.NumberFormat(INTL_LOCALE[locale]).format(value);
}

export function formatPercent(fraction: number, locale: Locale): string {
  return new Intl.NumberFormat(INTL_LOCALE[locale], {
    style: 'percent',
    maximumFractionDigits: 0,
  }).format(fraction);
}

export function formatDecimal(value: number, locale: Locale, digits = 1): string {
  return new Intl.NumberFormat(INTL_LOCALE[locale], {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

/** `137` → "2 h 17 min". Under an hour, just minutes. */
export function formatDuration(minutes: number, t: Translator): string {
  const safe = Math.max(0, Math.round(minutes));
  const hours = Math.floor(safe / 60);
  const rest = safe % 60;
  if (hours === 0) return t('unit.minutesShort', { count: rest });
  if (rest === 0) return t('unit.hoursShort', { count: hours });
  return t('unit.hoursMinutes', { hours, minutes: rest });
}

/**
 * Long totals read better as whole hours: 41 h, not 41 h 12 min. Used for
 * lifetime watch time, where the minutes are noise.
 */
export function formatHours(minutes: number, locale: Locale, t: Translator): string {
  const hours = Math.round(Math.max(0, minutes) / 60);
  return t('unit.hoursShort', { count: formatNumber(hours, locale) });
}

export function formatDate(iso: string, locale: Locale): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(INTL_LOCALE[locale], {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

export function formatDateTime(iso: string, locale: Locale): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(INTL_LOCALE[locale], {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/** `2026-07` → "Jul 2026". */
export function formatMonth(monthKey: string, locale: Locale, short = true): string {
  const [year, month] = monthKey.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(date.getTime())) return monthKey;
  return new Intl.DateTimeFormat(INTL_LOCALE[locale], {
    month: short ? 'short' : 'long',
    year: 'numeric',
  }).format(date);
}

/** Just the month initial-ish label for a dense axis: "Jul". */
export function formatMonthShort(monthKey: string, locale: Locale): string {
  const [year, month] = monthKey.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(date.getTime())) return monthKey;
  return new Intl.DateTimeFormat(INTL_LOCALE[locale], { month: 'short' }).format(date);
}

/**
 * "Today", "Yesterday", "3 days ago", and beyond a week the plain date —
 * relative phrasing stops being helpful once you have to count.
 */
export function formatRelativeDate(iso: string, locale: Locale, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';

  const startOfDay = (value: Date) =>
    new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000);

  if (days >= 0 && days <= 6) {
    const relative = new Intl.RelativeTimeFormat(INTL_LOCALE[locale], { numeric: 'auto' });
    return relative.format(-days, 'day');
  }
  return formatDate(iso, locale);
}

/** ISO date (`yyyy-mm-dd`) for a Date, in local time. */
export function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Combine a `yyyy-mm-dd` picker value with a time-of-day.
 *
 * Picking "yesterday" should not stamp midnight — that skews a month boundary
 * and reads oddly in the history list — so today's date keeps the current
 * clock time and past dates land at 20:00, when a disc actually goes in.
 */
export function dateInputToIso(value: string, now: Date = new Date()): string {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return now.toISOString();

  const isToday =
    year === now.getFullYear() && month === now.getMonth() + 1 && day === now.getDate();
  const date = isToday
    ? new Date(year, month - 1, day, now.getHours(), now.getMinutes())
    : new Date(year, month - 1, day, 20, 0);

  return date.toISOString();
}
